/**
 * 牛只模块：市场牛只列表 / 详情 / 公开溯源
 */
import { get } from './http';
import { computeAgeDays } from '@/utils/format';
import type { Cattle, CattleDetail, CattleQuery, Paginated, TraceEvent } from '@/types';

/**
 * 统一补齐后端未返回的展示字段（images/age_days）。
 * 后端 cattle 无 images/age_days/trace_score/distance_km/seller 字段：
 *   - images 缺失时回退 face_image_url 单图（沿用既有兜底）；
 *   - age_days 按 birth_date 折算；
 *   - trace_score/distance_km/seller 由展示层各自兜底（0 / '-' / farm_name）。
 */
export function normalizeCattle(cattle: Cattle): Cattle {
  const images =
    cattle.images && cattle.images.length > 0
      ? cattle.images
      : cattle.face_image_url
        ? [cattle.face_image_url]
        : [];
  return {
    ...cattle,
    images,
    age_days: cattle.age_days ?? computeAgeDays(cattle.birth_date),
  };
}

/** 牛只市场列表（公开接口） */
export async function listCattle(params: CattleQuery = {}): Promise<Paginated<Cattle>> {
  const data = await get<Paginated<Cattle>>('/public/cattle/list', {
    page: params.page ?? 1,
    page_size: params.page_size ?? 20,
    keyword: params.q,
    breed: params.breed,
    gender: params.gender,
    min_weight: params.min_weight,
    max_weight: params.max_weight,
    min_price: params.min_price,
    max_price: params.max_price,
    region: params.region,
    trade_mode: params.trade_mode,
    quarantine: params.quarantine,
    sort: params.sort,
    category: params.category,
  });
  return { ...data, list: (data.list ?? []).map(normalizeCattle) };
}

/** 牛只详情（公开详情含溯源记录，后端返回 { cattle, traces }） */
export async function getCattle(id: number): Promise<CattleDetail> {
  const data = await get<CattleDetail>(`/public/cattle/${id}`);
  return { ...data, cattle: normalizeCattle(data.cattle) };
}

/** 牛只溯源记录（受保护接口，需 cattle:read 权限；详情页优先用 getCattle().traces） */
export function getTrace(cattleId: number): Promise<TraceEvent[]> {
  return get<TraceEvent[]>(`/cattle/${cattleId}/trace`);
}
